import { useCallback } from "react";
import { useFormContext } from "@leagueplatform/web-common";
import {
  StackLayout,
  ParagraphText,
  HeadingText,
  Box,
} from "@leagueplatform/genesis-core";
import { PreferenceCheckboxSection } from "components/form-elements/preference-checkbox-section.component";
import type { TransformedSection } from "types/types";
import type { CommunicationPreferencesValues } from "./communications-section.component";

export interface CommunicationsFormContentProps {
  data: TransformedSection[] | undefined;
  isLoading: boolean;
  isEnableTextNotificationsModalOpen: boolean;
  onOpenEnableTextNotificationsModal: () => void;
}

// Helper function to convert preference ID (kebab-case) to field name (camelCase)
const getFieldNameFromPreferenceId = (preferenceId: string): string =>
  preferenceId.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

export const CommunicationsFormContent = ({
  data,
  isLoading,
  isEnableTextNotificationsModalOpen,
  onOpenEnableTextNotificationsModal,
}: CommunicationsFormContentProps) => {
  const { watch, setValue } = useFormContext<CommunicationPreferencesValues>();
  const watchedValues = watch();

  const createCheckedHandler = useCallback(
    (field: keyof CommunicationPreferencesValues) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(field, event.target.checked, { shouldDirty: true });
      },
    [setValue]
  );

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onOpenEnableTextNotificationsModal();
      } else {
        setValue("textNotification", false, { shouldDirty: true });
      }
    },
    [setValue, onOpenEnableTextNotificationsModal]
  );

  return (
    <StackLayout spacing="$two" horizontalAlignment="stretch">
      {data?.map((section) => (
        <Box key={section.section}>
          <StackLayout spacing="$half" css={{ marginBlockEnd: "$oneAndHalf" }}>
            <HeadingText size="md" level="4">
              {section.sectionName}
            </HeadingText>
            {section.description && (
              <ParagraphText size="sm">{section.description}</ParagraphText>
            )}
          </StackLayout>
          <StackLayout spacing="$one">
            {section.preferences.map((pref) => {
              const fieldName = getFieldNameFromPreferenceId(pref.id);
              const checkedValue =
                watchedValues[
                  fieldName as keyof CommunicationPreferencesValues
                ];
              const isTextNotification = fieldName === "textNotification";
              return (
                <PreferenceCheckboxSection
                  key={pref.id}
                  id={`checkbox-${pref.id}`}
                  name={fieldName}
                  title={pref.name}
                  label={pref.description}
                  hint={pref.caption}
                  checked={Boolean(checkedValue)}
                  onChange={
                    isTextNotification
                      ? handleTextChange
                      : createCheckedHandler(
                          fieldName as keyof CommunicationPreferencesValues
                        )
                  }
                  disabled={isLoading}
                  loading={
                    isTextNotification
                      ? isEnableTextNotificationsModalOpen
                      : undefined
                  }
                />
              );
            })}
          </StackLayout>
        </Box>
      ))}
    </StackLayout>
  );
};
